import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  LuGlobe,
  LuPlus,
  LuStar,
  LuCircleCheck,
  LuCircleX,
  LuCopy,
  LuCheck,
  LuChevronDown,
  LuTriangleAlert,
  LuArrowRight,
  LuShieldCheck,
} from "react-icons/lu";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";

interface DomainRow {
  id: string;
  domain: string;
  enabled: boolean;
  priority: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

const DOMAIN_PATTERN = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

const MX_RECORDS = [
  { priority: 10, value: "route1.mx.cloudflare.net" },
  { priority: 20, value: "route2.mx.cloudflare.net" },
  { priority: 30, value: "route3.mx.cloudflare.net" },
];
const SPF_RECORD = "v=spf1 include:_spf.mx.cloudflare.net ~all";

export function DomainsPage() {
  const qc = useQueryClient();
  const [domain, setDomain] = useState("");
  const [priority, setPriority] = useState(100);
  const [enabled, setEnabled] = useState(true);
  const [setupOpen, setSetupOpen] = useState(true);

  const domainsQuery = useQuery({
    queryKey: ["console", "domains"],
    queryFn: () => api.domains.list(),
  });

  const createMutation = useMutation({
    mutationFn: api.domains.create,
    onSuccess: () => {
      toast.success(`${domain} added.`);
      setDomain("");
      qc.invalidateQueries({ queryKey: ["console", "domains"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.domains.update>[1] }) =>
      api.domains.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["console", "domains"] }),
    onError: (err: Error) => toast.error(err.message),
  });

  const trimmedDomain = domain.trim().toLowerCase();
  const isValid = DOMAIN_PATTERN.test(trimmedDomain);
  const alreadyExists = domainsQuery.data?.domains.some(
    (d) => d.domain.toLowerCase() === trimmedDomain,
  );

  function handleAdd() {
    if (!isValid || alreadyExists || createMutation.isPending) return;
    createMutation.mutate({ domain: trimmedDomain, priority, enabled });
  }

  return (
    <>
      <PageHeader
        icon={LuGlobe}
        title="Public domains"
        description="Domains the inbox creator picks from. Higher priority = more frequent random pick."
      />

      <div className="p-8 space-y-6">
        {/* Setup guide (collapsible) */}
        <Card className="bg-[var(--color-surface)]">
          <button
            type="button"
            onClick={() => setSetupOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-[rgba(139,92,246,0.1)] border border-[rgba(139,92,246,0.25)] flex items-center justify-center">
                <LuShieldCheck className="w-4 h-4 text-[var(--color-accent-light)]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--color-text)]">
                  How to add a new domain
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                  4 steps. Once DNS propagates, paste the domain below.
                </p>
              </div>
            </div>
            <LuChevronDown
              className={`w-4 h-4 text-[var(--color-text-muted)] transition-transform ${
                setupOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {setupOpen && (
            <div className="px-5 pb-5 space-y-4 border-t border-[var(--color-border)] pt-4">
              <Step
                n={1}
                title="Buy and add the domain to Cloudflare"
                description="Any registrar works. Add it to the same Cloudflare account that hosts destroy-network-worker so Email Routing can be enabled."
              />

              <Step
                n={2}
                title="Set MX records"
                description="Cloudflare Email Routing requires three MX records on the apex. Set these in Cloudflare DNS:"
              >
                <DnsTable
                  rows={MX_RECORDS.map((r) => ({
                    type: "MX",
                    name: "@",
                    priority: String(r.priority),
                    value: r.value,
                  }))}
                />
              </Step>

              <Step
                n={3}
                title="Add the SPF record"
                description="One TXT record on the apex authorises Cloudflare to send on behalf of the domain."
              >
                <DnsTable
                  rows={[
                    {
                      type: "TXT",
                      name: "@",
                      priority: "—",
                      value: SPF_RECORD,
                    },
                  ]}
                />
              </Step>

              <Step
                n={4}
                title="Enable Email Routing + catch-all"
                description="Cloudflare dashboard → Email → Email Routing → enable, then add a catch-all rule pointing to the destroy-network worker."
              >
                <div className="text-xs text-[var(--color-text-muted)] bg-[var(--color-bg)] rounded-md border border-[var(--color-border)] px-3 py-2 font-mono">
                  Catch-all: <span className="text-[var(--color-accent-light)]">Send to Worker</span>{" "}
                  → <span className="text-[var(--color-text)]">destroy-network-worker</span>
                </div>
              </Step>

              <div className="flex items-start gap-2 p-3 rounded-md bg-[rgba(250,176,5,0.06)] border border-[rgba(250,176,5,0.25)] text-xs text-[var(--color-text-muted)]">
                <LuTriangleAlert className="w-4 h-4 text-[#fcd34d] shrink-0 mt-0.5" />
                <span>
                  DNS propagation can take up to 24 hours. Use the{" "}
                  <span className="text-[var(--color-accent-light)]">Domain health</span> page to
                  verify MX / SPF after adding.
                </span>
              </div>
            </div>
          )}
        </Card>

        {/* Add domain form */}
        <Card>
          <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center gap-2">
            <LuPlus className="w-4 h-4 text-[var(--color-accent-light)]" />
            <p className="text-sm font-semibold text-[var(--color-text)]">Register domain</p>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[2fr_auto_auto_auto] gap-3 items-end">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-2">
                  Domain
                </label>
                <div className="relative">
                  <LuGlobe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-subtle)] pointer-events-none" />
                  <input
                    type="text"
                    placeholder="example.network"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAdd();
                    }}
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-subtle)] focus:outline-none focus:border-[var(--color-accent)] font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-2">
                  Priority
                </label>
                <input
                  type="number"
                  min={0}
                  max={1000}
                  value={priority}
                  onChange={(e) =>
                    setPriority(Math.max(0, Math.min(1000, parseInt(e.target.value, 10) || 0)))
                  }
                  className="w-24 px-3 py-2 text-sm rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)] tabular-nums"
                />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)] mb-2">
                  Enabled
                </label>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[var(--color-bg)] border border-[var(--color-border)] cursor-pointer h-[38px]">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    className="accent-[var(--color-accent)]"
                  />
                  <span className="text-xs text-[var(--color-text-muted)]">on</span>
                </label>
              </div>

              <Button
                onClick={handleAdd}
                disabled={!isValid || alreadyExists || createMutation.isPending}
              >
                <LuPlus className="w-4 h-4" />
                {createMutation.isPending ? "Adding..." : "Add domain"}
              </Button>
            </div>

            {/* Validation feedback */}
            {domain.length > 0 && (
              <div className="text-xs">
                {alreadyExists ? (
                  <p className="text-[var(--color-warning)] flex items-center gap-1.5">
                    <LuTriangleAlert className="w-3.5 h-3.5" />
                    Already registered.
                  </p>
                ) : !isValid ? (
                  <p className="text-[var(--color-text-subtle)] flex items-center gap-1.5">
                    <LuTriangleAlert className="w-3.5 h-3.5" />
                    Not a valid domain (e.g. <code className="text-[var(--color-text-muted)]">example.com</code>).
                  </p>
                ) : (
                  <p className="text-[#6ee7b7] flex items-center gap-1.5">
                    <LuCircleCheck className="w-3.5 h-3.5" />
                    Looks good. Higher priority shows up more often in random picks.
                  </p>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Existing domains */}
        <Card>
          <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LuGlobe className="w-4 h-4 text-[var(--color-text-subtle)]" />
              <p className="text-sm font-semibold text-[var(--color-text)]">
                Registered domains
                {domainsQuery.data && (
                  <span className="ml-2 text-xs font-normal text-[var(--color-text-subtle)]">
                    {domainsQuery.data.domains.length}
                  </span>
                )}
              </p>
            </div>
          </div>

          {domainsQuery.isLoading && (
            <div className="p-5 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}

          {domainsQuery.data && domainsQuery.data.domains.length === 0 && (
            <div className="px-5 py-10 text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-[var(--color-elevated)] flex items-center justify-center">
                <LuGlobe className="w-5 h-5 text-[var(--color-text-subtle)]" />
              </div>
              <p className="text-sm text-[var(--color-text-muted)]">
                No domains registered yet. Add one above.
              </p>
            </div>
          )}

          {domainsQuery.data && domainsQuery.data.domains.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg)]/40">
                <tr className="text-left text-[var(--color-text-subtle)] border-b border-[var(--color-border)]">
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium">
                    Domain
                  </th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium text-right">
                    Priority
                  </th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium">
                    Status
                  </th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium">
                    Default
                  </th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em] font-medium text-right">
                    Updated
                  </th>
                </tr>
              </thead>
              <tbody>
                {(domainsQuery.data.domains as DomainRow[]).map((d) => (
                  <DomainRowView
                    key={d.id}
                    row={d}
                    onToggleEnabled={(checked) =>
                      updateMutation.mutate({ id: d.id, body: { enabled: checked } })
                    }
                    onMakeDefault={() =>
                      updateMutation.mutate({ id: d.id, body: { isDefault: true } })
                    }
                  />
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}

function Step({
  n,
  title,
  description,
  children,
}: {
  n: number;
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="w-7 h-7 rounded-full bg-[var(--color-bg)] border border-[var(--color-border)] flex items-center justify-center text-xs font-semibold text-[var(--color-accent-light)] shrink-0 mt-0.5">
        {n}
      </div>
      <div className="flex-1 space-y-2 min-w-0">
        <div>
          <p className="text-sm font-medium text-[var(--color-text)]">{title}</p>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{description}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

interface DnsRow {
  type: string;
  name: string;
  priority: string;
  value: string;
}

function DnsTable({ rows }: { rows: DnsRow[] }) {
  return (
    <div className="rounded-md border border-[var(--color-border)] overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-[var(--color-bg)]/40">
          <tr className="text-left text-[var(--color-text-subtle)]">
            <th className="px-3 py-2 font-medium">Type</th>
            <th className="px-3 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Priority</th>
            <th className="px-3 py-2 font-medium">Value</th>
            <th className="px-3 py-2 w-8" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              className="border-t border-[var(--color-border)]"
            >
              <td className="px-3 py-2 font-mono text-[var(--color-accent-light)]">{r.type}</td>
              <td className="px-3 py-2 font-mono text-[var(--color-text)]">{r.name}</td>
              <td className="px-3 py-2 font-mono text-[var(--color-text-muted)] tabular-nums">
                {r.priority}
              </td>
              <td className="px-3 py-2 font-mono text-[var(--color-text)] truncate max-w-[280px]">
                {r.value}
              </td>
              <td className="px-3 py-2">
                <CopyButton value={r.value} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          toast.error("Copy failed");
        }
      }}
      className="text-[var(--color-text-subtle)] hover:text-[var(--color-text)] transition-colors p-1"
      title={copied ? "Copied" : "Copy"}
    >
      {copied ? <LuCheck className="w-3.5 h-3.5 text-[#6ee7b7]" /> : <LuCopy className="w-3.5 h-3.5" />}
    </button>
  );
}

function DomainRowView({
  row,
  onToggleEnabled,
  onMakeDefault,
}: {
  row: DomainRow;
  onToggleEnabled: (checked: boolean) => void;
  onMakeDefault: () => void;
}) {
  return (
    <tr className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-elevated)]/30 transition-colors">
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <code className="font-mono text-sm text-[var(--color-text)]">{row.domain}</code>
          {row.isDefault && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded border bg-[rgba(250,176,5,0.08)] border-[rgba(250,176,5,0.3)] text-[#fcd34d]">
              <LuStar className="w-3 h-3" /> Default
            </span>
          )}
        </div>
      </td>

      <td className="px-5 py-3 text-right tabular-nums text-[var(--color-text-muted)] font-mono">
        {row.priority}
      </td>

      <td className="px-5 py-3">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <span className="relative inline-block w-9 h-5">
            <input
              type="checkbox"
              checked={row.enabled}
              onChange={(e) => onToggleEnabled(e.target.checked)}
              className="peer sr-only"
            />
            <span className="absolute inset-0 rounded-full bg-[var(--color-elevated)] border border-[var(--color-border)] peer-checked:bg-[rgba(139,92,246,0.5)] peer-checked:border-[var(--color-accent)] transition-colors" />
            <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-[var(--color-text-muted)] peer-checked:translate-x-4 peer-checked:bg-white transition-transform" />
          </span>
          <span className="text-xs text-[var(--color-text-muted)] tabular-nums">
            {row.enabled ? (
              <LuCircleCheck className="w-3.5 h-3.5 text-[#6ee7b7]" />
            ) : (
              <LuCircleX className="w-3.5 h-3.5 text-[var(--color-text-subtle)]" />
            )}
          </span>
        </label>
      </td>

      <td className="px-5 py-3">
        {row.isDefault ? (
          <span className="text-[10px] uppercase tracking-wider text-[#6ee7b7]">current</span>
        ) : (
          <button
            type="button"
            onClick={onMakeDefault}
            className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent-light)] transition-colors"
          >
            <LuArrowRight className="w-3 h-3" />
            Make default
          </button>
        )}
      </td>

      <td className="px-5 py-3 text-right text-xs text-[var(--color-text-subtle)] font-mono tabular-nums">
        {new Date(row.updatedAt).toLocaleDateString()}
      </td>
    </tr>
  );
}
